import type { components } from "@/api-schema";
import {
  CustomerType,
  ServiceHistoryStatus,
  VehicleBuyInSignatureDevice,
  VehicleBuyInType,
  VehicleUsage,
} from "@/api-schema-enums";
import type { VehicleBuyInCreateBody } from "@/common/api/vehicle-buy-in/use-vehicle-buy-in-create.mutation";
import {
  getDateInputValidationMessage,
  getMonthInputValidationMessage,
  validateDateInputString,
  validateMonthInputString,
} from "@/common/helpers/date-input";
import { sortByRenderedLabel } from "@/common/helpers/sort-by-rendered-label";
import { cn } from "@/common/helpers/utils";
import { VEHICLE_COLOR_LABELS } from "@/common/helpers/vehicle-type-labels";
import { AddressSearch } from "@/components/address-form-fields/address-search";
import { automaticCellRenderer } from "@/components/data-table/_helpers/cell-renderer";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DatePickerInput } from "@/components/ui/date-picker-input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormCard, FormCardActions, FormCardSection } from "@/components/ui/form-card";
import { Input, InputTextAlign } from "@/components/ui/input";
import { LocalizedNumberInput } from "@/components/ui/localized-number-input";
import { MonthPickerInput } from "@/components/ui/month-picker-input";
import { RadioGroup, RadioGroupCardItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { RelocationOrderDetailInfoRow } from "@/views/relocation-order-detail/_components/relocation-order-detail-info-row";
import { DetailInfoRow } from "@/views/vehicle-valuation-detail/_components/detail-info-row";
import { ChevronDownIcon, ChevronUpIcon, InfoIcon, UserRoundSearch } from "lucide-react";
import { useId, useMemo, useState, type ReactNode } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  getVehicleAccidentStateLabel,
  getVehicleBooleanLabel,
  shouldShowVehicleAccidentDetails,
} from "../../vehicle-valuation/_helpers/vehicle-valuation-metadata";
import {
  buildVehicleBuyInCreateBody,
  calculateVehicleBuyInVatAmount,
  filterValidVehicleBuyInCustomerNumberIdentifiers,
  getBranchSnapshot,
  getVehicleBuyInBranchDeviceError,
  getVehicleBuyInCustomerEmailError,
  getVehicleBuyInFormDefaults,
  isValidVehicleBuyInCustomerNumber,
  mapCustomerToVehicleBuyInForm,
  type VehicleBuyInCustomer,
  type VehicleBuyInFormValues,
} from "../_helpers/vehicle-buy-in-form.helpers";
import { VehicleBuyInCustomerNumberDialog } from "./vehicle-buy-in-customer-number-dialog";
import { VehicleBuyInCustomerPrefillDialog } from "./vehicle-buy-in-customer-prefill-dialog";

interface FieldControlProps {
  id: string;
  "aria-describedby": string | undefined;
  "aria-invalid": true | undefined;
}

interface FieldProps {
  children: (controlProps: FieldControlProps) => ReactNode;
  describedBy?: string;
  error?: string;
  hideErrorMessage?: boolean;
  label: string;
  required?: boolean;
  className?: string;
}

function Field({ children, describedBy, error, hideErrorMessage = false, label, required, className }: FieldProps) {
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const ariaDescribedBy = [describedBy, error && !hideErrorMessage ? errorId : undefined].filter(Boolean).join(" ");

  return (
    <div className={cn("space-y-1.5", className)}>
      <label htmlFor={inputId} className="text-sm font-medium">
        {label}
        {required ? <span className="text-error ml-1">*</span> : null}
      </label>
      {children({
        id: inputId,
        "aria-describedby": ariaDescribedBy || undefined,
        "aria-invalid": error ? true : undefined,
      })}
      {error && !hideErrorMessage ? (
        <p id={errorId} role="alert" className="text-error text-xs">
          {error}
        </p>
      ) : null}
    </div>
  );
}

interface ReadOnlyDetailsProps {
  children: ReactNode;
  className?: string;
  contentClassName?: string;
}

function ReadOnlyDetails({ children, className, contentClassName }: ReadOnlyDetailsProps) {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className={className}>
      <CollapsibleTrigger asChild>
        <Button
          type="button"
          size={ButtonSize.Sm}
          variant={ButtonVariant.Ghost}
          colorVariant={ButtonColorVariant.Secondary}
        >
          {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
          {t(isOpen ? "common:ShowLess" : "common:ShowMore")}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className={cn("pt-3", contentClassName)}>{children}</CollapsibleContent>
    </Collapsible>
  );
}

interface VehicleBuyInFormProps {
  valuation: components["schemas"]["VehicleValuation"];
  vehicle: components["schemas"]["Vehicle"];
  createData: components["schemas"]["VehicleBuyInCreateDataDto"];
  vehicleValuationUuid: string;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (body: VehicleBuyInCreateBody) => Promise<void>;
}

export function VehicleBuyInForm({
  valuation,
  vehicle,
  createData,
  vehicleValuationUuid,
  isPending,
  onCancel,
  onSubmit,
}: VehicleBuyInFormProps) {
  const { t, i18n } = useTranslation(["fab", "common", "masterdata"]);
  const branchDeviceErrorId = useId();
  const requiredMessage = t("common:FieldIsRequired");
  const defaultValues = useMemo(
    () =>
      getVehicleBuyInFormDefaults({
        vehicleValuationUuid,
        valuation,
        vehicle,
        createData,
      }),
    [createData, valuation, vehicle, vehicleValuationUuid],
  );
  const form = useForm<VehicleBuyInFormValues>({
    mode: "onChange",
    defaultValues,
  });
  const [isCustomerPrefillOpen, setIsCustomerPrefillOpen] = useState(false);
  const [isCustomerNumberSelectionOpen, setIsCustomerNumberSelectionOpen] = useState(false);
  const [customerIdentifiers, setCustomerIdentifiers] = useState<
    NonNullable<VehicleBuyInCustomer["customerIdentifiers"]>
  >([]);
  const buyInType = useWatch({ control: form.control, name: "type" });
  const customerType = useWatch({
    control: form.control,
    name: "customer.type",
  });
  const signatureDevice = useWatch({
    control: form.control,
    name: "signatureDevice",
  });
  const branchUuid = useWatch({ control: form.control, name: "branchUuid" });
  const purchasePrice = useWatch({
    control: form.control,
    name: "purchase.price",
  });
  const branch = useMemo(() => getBranchSnapshot(createData, branchUuid), [branchUuid, createData]);
  const branchOptions = useMemo(
    () => sortByRenderedLabel(createData.branches, (option) => `${option.name} · ${option.companyName}`),
    [createData.branches],
  );
  const employee = defaultValues.employee;
  const branchDeviceError =
    signatureDevice === VehicleBuyInSignatureDevice.Company && branchUuid
      ? getVehicleBuyInBranchDeviceError(branch.signatureTabletEmail, employee.email)
      : undefined;
  const displayVehicle = defaultValues.vehicle;
  const vatDeductible = displayVehicle.vatDeductible;
  const exchangeEngineInstalled = displayVehicle.exchangeEngineInstalled;
  const shouldShowAccidentDetails = shouldShowVehicleAccidentDetails(displayVehicle.accident.state);
  const vatAmount = calculateVehicleBuyInVatAmount(purchasePrice, vatDeductible);
  const customerTypeOptions = useMemo(
    () => [
      { value: CustomerType.Private, label: t("fab:PrivateCustomer") },
      { value: CustomerType.Business, label: t("fab:BusinessCustomer") },
    ],
    [t],
  );
  const vehicleBuyInTypeOptions = useMemo(
    () => [
      {
        value: VehicleBuyInType.TradeIn,
        label: t("fab:VehicleBuyInTypeTradeIn"),
      },
      {
        value: VehicleBuyInType.Purchase,
        label: t("fab:VehicleBuyInTypePurchase"),
      },
    ],
    [t],
  );
  const signatureDeviceOptions = useMemo(
    () => [
      {
        value: VehicleBuyInSignatureDevice.Customer,
        label: t("fab:UseCustomerEmailAddress"),
        description: t("fab:UseCustomerEmailAddressDescription"),
      },
      {
        value: VehicleBuyInSignatureDevice.Company,
        label: t("fab:UseBranchDevice"),
        description: t("fab:UseBranchDeviceDescription"),
      },
    ],
    [t],
  );
  const vehicleUsageOptions = useMemo(
    () => [
      {
        value: VehicleUsage.UNKNOWN,
        label: t("masterdata:VehicleUsageUnknown"),
      },
      {
        value: VehicleUsage.RegularPersonalCommercial,
        label: t("masterdata:VehicleUsageRegular"),
      },
      { value: VehicleUsage.RENTAL, label: t("masterdata:VehicleUsageRental") },
      {
        value: VehicleUsage.TaxiRideHailing,
        label: t("masterdata:VehicleUsageTaxi"),
      },
      {
        value: VehicleUsage.DrivingSchool,
        label: t("masterdata:VehicleUsageDrivingSchool"),
      },
      { value: VehicleUsage.DEMO, label: t("masterdata:VehicleUsageDemo") },
      {
        value: VehicleUsage.FleetPool,
        label: t("masterdata:VehicleUsageFleetPool"),
      },
      {
        value: VehicleUsage.DeliveryCourier,
        label: t("masterdata:VehicleUsageDelivery"),
      },
      {
        value: VehicleUsage.SpecialPurpose,
        label: t("masterdata:VehicleUsageSpecialPurpose"),
      },
      {
        value: VehicleUsage.CONSTRUCTION,
        label: t("masterdata:VehicleUsageConstruction"),
      },
      {
        value: VehicleUsage.ManagementCompanyCar,
        label: t("masterdata:VehicleUsageManagementCompanyCar"),
      },
      {
        value: VehicleUsage.EmployeeCompanyCar,
        label: t("masterdata:VehicleUsageEmployeeCompanyCar"),
      },
    ],
    [t],
  );
  const serviceHistoryOptions = useMemo(
    () => [
      {
        value: ServiceHistoryStatus.UNKNOWN,
        label: t("masterdata:ServiceHistoryUnknown"),
      },
      {
        value: ServiceHistoryStatus.NONE,
        label: t("masterdata:ServiceHistoryNone"),
      },
      {
        value: ServiceHistoryStatus.PARTIAL,
        label: t("masterdata:ServiceHistoryPartial"),
      },
      {
        value: ServiceHistoryStatus.FullServiceHistory,
        label: t("masterdata:ServiceHistoryFull"),
      },
      {
        value: ServiceHistoryStatus.FullDealerServiceHistory,
        label: t("masterdata:ServiceHistoryFullDealer"),
      },
    ],
    [t],
  );
  const { errors, isValid } = form.formState;
  const required = { required: requiredMessage };
  const customerEmailRules = {
    validate: (value: string, values: VehicleBuyInFormValues) => {
      const error = getVehicleBuyInCustomerEmailError(value, values.signatureDevice, employee.email);
      return error ? t(error) : true;
    },
  };
  const requiredNumber = {
    required: requiredMessage,
    min: { value: 0, message: t("fab:ValueMustNotBeNegative") },
  };

  const optionalCustomerNumber = {
    setValueAs: (value: string) => value.trim(),
    validate: (value: string) =>
      !value || isValidVehicleBuyInCustomerNumber(value) || t("fab:CustomerNumberMustBeNumeric"),
  };

  const requiredPastDate = {
    required: requiredMessage,
    validate: (value: string) => {
      const error = validateDateInputString(value, {
        disallowFutureDate: true,
      });
      return error ? getDateInputValidationMessage(error) : true;
    },
  };
  const requiredCurrentOrFutureMonth = {
    required: requiredMessage,
    validate: (value: string) => {
      const error = validateMonthInputString(value, {
        pastMonthsDisabled: true,
      });
      return error ? getMonthInputValidationMessage(error) : true;
    },
  };
  const handleCustomerPrefill = (customer: VehicleBuyInCustomer) => {
    const identifiers = filterValidVehicleBuyInCustomerNumberIdentifiers(customer.customerIdentifiers ?? []);
    form.setValue("customer", mapCustomerToVehicleBuyInForm(customer), {
      shouldDirty: true,
      shouldValidate: true,
    });
    setCustomerIdentifiers(identifiers);
    setIsCustomerNumberSelectionOpen(identifiers.length > 0);
    void form.trigger("customer");
  };

  const handleCustomerNumberSelect = (customerNumber: string) => {
    form.setValue("customer.customerNumber", customerNumber, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleVehicleBuyInTypeChange = (value: string) => {
    if (value !== VehicleBuyInType.TradeIn && value !== VehicleBuyInType.Purchase) return;

    form.setValue("type", value, { shouldDirty: true, shouldValidate: true });
    if (value === VehicleBuyInType.Purchase) {
      form.setValue("tradeInReference", "", { shouldDirty: true });
    }
  };

  const handleSignatureDeviceChange = (value: string) => {
    if (value !== VehicleBuyInSignatureDevice.Customer && value !== VehicleBuyInSignatureDevice.Company) return;

    form.setValue("signatureDevice", value, {
      shouldDirty: true,
      shouldValidate: true,
    });
    void form.trigger("customer.email");
  };

  const handleBranchChange = (value: string) => {
    if (value === form.getValues("branchUuid")) return;

    form.setValue("branchUuid", value, {
      shouldDirty: true,
      shouldValidate: true,
    });
    if (form.getValues("signatureDevice") === VehicleBuyInSignatureDevice.Company) {
      form.setValue("signatureDevice", "", {
        shouldDirty: true,
        shouldValidate: true,
      });
      void form.trigger("customer.email");
    }
  };

  const handleSubmit = async (values: VehicleBuyInFormValues) => {
    const selectedBranch = getBranchSnapshot(createData, values.branchUuid);
    if (
      values.signatureDevice === VehicleBuyInSignatureDevice.Company &&
      getVehicleBuyInBranchDeviceError(selectedBranch.signatureTabletEmail, employee.email)
    ) {
      return;
    }

    await onSubmit(buildVehicleBuyInCreateBody(values));
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormCard title={t("fab:ContractPartners")} contentClassName="space-y-4">
          <FormCardSection
            title={t("fab:BranchAndCompany")}
            contentClassName="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2"
          >
            <FormField
              control={form.control}
              name="branchUuid"
              rules={required}
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormLabel required>{t("fab:Branch")}</FormLabel>
                  <Select onValueChange={handleBranchChange} value={field.value ?? ""}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("fab:SelectBranch")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {branchOptions.map((branch) => (
                        <SelectItem key={branch.uuid} value={branch.uuid}>
                          {branch.name} · {branch.companyName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <ReadOnlyDetails className="sm:col-span-2" contentClassName="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              <RelocationOrderDetailInfoRow label={t("fab:BranchName")} value={branch.name} />
              <RelocationOrderDetailInfoRow label={t("fab:CompanyName")} value={branch.companyName} />
              <RelocationOrderDetailInfoRow label={t("common:Street")} value={branch.address.street} />
              <RelocationOrderDetailInfoRow label={t("common:HouseNumber")} value={branch.address.houseNumber} />
              <RelocationOrderDetailInfoRow label={t("common:PostalCode")} value={branch.address.postalCode} />
              <RelocationOrderDetailInfoRow label={t("common:City")} value={branch.address.city} />
              <RelocationOrderDetailInfoRow label={t("common:Country")} value={branch.address.country} />
            </ReadOnlyDetails>
          </FormCardSection>

          <FormCardSection title={t("fab:Employee")}>
            <ReadOnlyDetails contentClassName="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              <RelocationOrderDetailInfoRow label={t("common:FirstName")} value={employee.firstName} />
              <RelocationOrderDetailInfoRow label={t("common:LastName")} value={employee.lastName} />
              <RelocationOrderDetailInfoRow label={t("common:Email")} value={employee.email} />
              <RelocationOrderDetailInfoRow label={t("common:Occupation")} value={employee.occupation} />
            </ReadOnlyDetails>
          </FormCardSection>

          <FormCardSection title={t("fab:Customer")} contentClassName="grid gap-3 md:grid-cols-2">
            <div className="border-border bg-muted/30 flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between md:col-span-2">
              <div className="min-w-0">
                <p className="text-sm font-medium">{t("fab:CustomerDataEntry")}</p>
                <p className="text-muted-foreground text-sm">{t("fab:CustomerPrefillHint")}</p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                <Button
                  type="button"
                  variant={ButtonVariant.Outlined}
                  colorVariant={ButtonColorVariant.Secondary}
                  onClick={() => setIsCustomerPrefillOpen(true)}
                >
                  <UserRoundSearch className="mr-2 h-4 w-4" />
                  {t("fab:PrefillFromExistingCustomer")}
                </Button>
              </div>
            </div>
            <FormField
              control={form.control}
              name="customer.type"
              rules={required}
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>{t("fab:CustomerType")}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value ?? ""}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {customerTypeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Field label={t("fab:CustomerNumber")} error={errors.customer?.customerNumber?.message}>
              {(controlProps) => (
                <Input {...form.register("customer.customerNumber", optionalCustomerNumber)} {...controlProps} />
              )}
            </Field>
            {customerType === CustomerType.Business ? (
              <>
                <Field label={t("fab:CompanyName")} required error={errors.customer?.companyName?.message}>
                  {(controlProps) => <Input {...form.register("customer.companyName", required)} {...controlProps} />}
                </Field>
                <Field label={t("fab:RegistrationNumber")}>
                  {(controlProps) => <Input {...form.register("customer.registrationNumber")} {...controlProps} />}
                </Field>
              </>
            ) : null}
            <Field label={t("common:FirstName")} required error={errors.customer?.firstName?.message}>
              {(controlProps) => (
                <Input required {...form.register("customer.firstName", required)} {...controlProps} />
              )}
            </Field>
            <Field label={t("common:LastName")} required error={errors.customer?.lastName?.message}>
              {(controlProps) => <Input required {...form.register("customer.lastName", required)} {...controlProps} />}
            </Field>
            {customerType === CustomerType.Private ? (
              <FormField
                control={form.control}
                name="customer.birthDate"
                rules={requiredPastDate}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t("fab:BirthDate")}</FormLabel>
                    <FormControl>
                      <DatePickerInput
                        ref={field.ref}
                        name={field.name}
                        value={field.value ?? ""}
                        onChange={(value) =>
                          form.setValue("customer.birthDate", value, {
                            shouldValidate: true,
                            shouldDirty: true,
                          })
                        }
                        onBlur={() => {
                          field.onBlur();
                          void form.trigger("customer.birthDate");
                        }}
                        futureDaysDisabled
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            <Field label={t("common:Phone")} required error={errors.customer?.phoneNumber?.message}>
              {(controlProps) => (
                <Input type="tel" {...form.register("customer.phoneNumber", required)} {...controlProps} />
              )}
            </Field>
            <div role="separator" className="border-border border-t md:col-span-2" />
            <div className="md:col-span-2">
              <AddressSearch
                onSelect={(address) => {
                  form.setValue(
                    "customer.address",
                    {
                      ...form.getValues("customer.address"),
                      street: address.street,
                      houseNumber: address.houseNumber,
                      postalCode: address.postalCode,
                      city: address.city,
                      country: address.countryCode,
                    },
                    { shouldDirty: true, shouldValidate: true },
                  );
                }}
              />
            </div>
            <Field label={t("common:AddressAddition")} className="md:col-span-2">
              {(controlProps) => <Input {...form.register("customer.address.houseExtension")} {...controlProps} />}
            </Field>
            <Field label={t("common:Street")} required error={errors.customer?.address?.street?.message}>
              {(controlProps) => <Input {...form.register("customer.address.street", required)} {...controlProps} />}
            </Field>
            <Field label={t("common:HouseNumber")} required error={errors.customer?.address?.houseNumber?.message}>
              {(controlProps) => (
                <Input {...form.register("customer.address.houseNumber", required)} {...controlProps} />
              )}
            </Field>
            <Field label={t("common:PostalCode")} required error={errors.customer?.address?.postalCode?.message}>
              {(controlProps) => (
                <Input {...form.register("customer.address.postalCode", required)} {...controlProps} />
              )}
            </Field>
            <Field label={t("common:City")} required error={errors.customer?.address?.city?.message}>
              {(controlProps) => <Input {...form.register("customer.address.city", required)} {...controlProps} />}
            </Field>
            <Field label={t("common:Country")} required error={errors.customer?.address?.country?.message}>
              {(controlProps) => <Input {...form.register("customer.address.country", required)} {...controlProps} />}
            </Field>
            <div role="separator" className="border-border border-t md:col-span-2" />
            <FormField
              control={form.control}
              name="signatureDevice"
              rules={required}
              render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel required>{t("fab:SignatureDevice")}</FormLabel>
                  <FormControl
                    aria-invalid={!!errors.signatureDevice || !!branchDeviceError}
                    aria-errormessage={branchDeviceError ? branchDeviceErrorId : undefined}
                  >
                    <RadioGroup
                      name={field.name}
                      value={field.value ?? ""}
                      onValueChange={handleSignatureDeviceChange}
                      onBlur={field.onBlur}
                      aria-label={t("fab:SignatureDevice")}
                      required
                      className="grid gap-3 md:grid-cols-2"
                    >
                      {signatureDeviceOptions.map((option) => (
                        <RadioGroupCardItem
                          key={option.value}
                          value={option.value}
                          selected={field.value === option.value}
                          label={option.label}
                          description={option.description}
                          aria-describedby={
                            option.value === VehicleBuyInSignatureDevice.Company && branchDeviceError
                              ? branchDeviceErrorId
                              : undefined
                          }
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Field
              label={t("common:Email")}
              required={signatureDevice !== VehicleBuyInSignatureDevice.Company}
              error={errors.customer?.email?.message}
            >
              {(controlProps) => (
                <Input type="email" {...form.register("customer.email", customerEmailRules)} {...controlProps} />
              )}
            </Field>
            {signatureDevice === VehicleBuyInSignatureDevice.Company ? (
              <Field
                label={t("masterdata:BranchSignatureTabletEmail")}
                describedBy={branchDeviceError ? branchDeviceErrorId : undefined}
              >
                {(controlProps) => (
                  <Input type="email" value={branch.signatureTabletEmail} disabled {...controlProps} />
                )}
              </Field>
            ) : null}
            {branchDeviceError ? (
              <Alert id={branchDeviceErrorId} variant="error" className="md:col-span-2">
                <AlertDescription>{t(branchDeviceError)}</AlertDescription>
              </Alert>
            ) : null}
          </FormCardSection>
        </FormCard>

        <FormCard title={t("fab:VehicleInformation")}>
          <ReadOnlyDetails contentClassName="space-y-4">
            <FormCardSection title={t("common:Vehicle")} contentClassName="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              <RelocationOrderDetailInfoRow label={t("fab:VIN")} value={displayVehicle.vin} mono />
              <RelocationOrderDetailInfoRow label={t("fab:LicensePlate")} value={displayVehicle.licensePlate} mono />
              <RelocationOrderDetailInfoRow label={t("fab:Brand")} value={displayVehicle.model.brandName} />
              <RelocationOrderDetailInfoRow label={t("fab:ModelFamily")} value={displayVehicle.model.modelFamilyName} />
              <RelocationOrderDetailInfoRow
                label={t("fab:ModelGeneration")}
                value={displayVehicle.model.modelGenerationName}
              />
              <RelocationOrderDetailInfoRow
                label={t("masterdata:ModelDescription")}
                value={displayVehicle.model.modelDescription}
              />
              <RelocationOrderDetailInfoRow label={t("fab:EngineKilowatts")} value={displayVehicle.engineKilowatts} />
              <RelocationOrderDetailInfoRow
                label={t("fab:Color")}
                value={
                  displayVehicle.color
                    ? (VEHICLE_COLOR_LABELS[displayVehicle.color]?.[i18n.language === "de" ? "de" : "en"] ??
                      displayVehicle.color)
                    : undefined
                }
              />
              <RelocationOrderDetailInfoRow
                label={t("fab:ManufacturerColor")}
                value={displayVehicle.manufacturerColor}
              />
              <RelocationOrderDetailInfoRow label={t("fab:NumberOfDoors")} value={displayVehicle.numberOfDoors} />
              <RelocationOrderDetailInfoRow
                label={t("common:RegistrationDate")}
                value={displayVehicle.registrationDate}
              />
              <RelocationOrderDetailInfoRow label={t("fab:PreviousHolders")} value={displayVehicle.numberOfHolders} />
              <RelocationOrderDetailInfoRow
                label={t("fab:VehicleUsage")}
                value={vehicleUsageOptions.find((option) => option.value === displayVehicle.usage)?.label}
              />
              <DetailInfoRow
                label={t("fab:CurrentMileage")}
                value={automaticCellRenderer(valuation.mileageUsed ?? vehicle.mileage)}
              />
              <RelocationOrderDetailInfoRow label={t("fab:HuDate")} value={displayVehicle.huDate} />
              <RelocationOrderDetailInfoRow
                label={t("fab:ServiceHistory")}
                value={serviceHistoryOptions.find((option) => option.value === displayVehicle.serviceHistory)?.label}
              />
              <RelocationOrderDetailInfoRow
                label={t("masterdata:VatDeductible")}
                value={getVehicleBooleanLabel(displayVehicle.vatDeductible, t)}
              />
              <RelocationOrderDetailInfoRow
                label={t("masterdata:Reimport")}
                value={getVehicleBooleanLabel(displayVehicle.reimport, t)}
              />
            </FormCardSection>

            <FormCardSection title={t("fab:Accident")} contentClassName="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              <RelocationOrderDetailInfoRow
                label={t("fab:AccidentState")}
                value={getVehicleAccidentStateLabel(displayVehicle.accident.state, t)}
              />
              {shouldShowAccidentDetails ? (
                <>
                  <DetailInfoRow
                    label={t("fab:AccidentValue")}
                    value={automaticCellRenderer(displayVehicle.accident.value, {
                      numberFormat: "currency",
                      numberDecimals: 2,
                    })}
                  />
                  <div className="sm:col-span-2">
                    <RelocationOrderDetailInfoRow
                      label={t("fab:AccidentDescription")}
                      value={displayVehicle.accident.description}
                    />
                  </div>
                </>
              ) : null}
            </FormCardSection>

            <FormCardSection
              title={t("fab:ExchangeEngine")}
              contentClassName="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2"
            >
              <div className="sm:col-span-2">
                <RelocationOrderDetailInfoRow
                  label={t("fab:ExchangeEngineInstalled")}
                  value={getVehicleBooleanLabel(displayVehicle.exchangeEngineInstalled, t)}
                />
              </div>
              {exchangeEngineInstalled ? (
                <>
                  <RelocationOrderDetailInfoRow
                    label={t("fab:ExchangeDate")}
                    value={displayVehicle.exchangeEngine.exchangeDate}
                  />
                  <DetailInfoRow
                    label={t("fab:VehicleMileageAtExchange")}
                    value={automaticCellRenderer(vehicle.exchangeEngine?.vehicleMileageAtExchange)}
                  />
                  <DetailInfoRow
                    label={t("fab:EngineMileageAtExchange")}
                    value={automaticCellRenderer(vehicle.exchangeEngine?.engineMileageAtExchange)}
                  />
                  <div className="sm:col-span-2">
                    <RelocationOrderDetailInfoRow
                      label={t("fab:ExchangeEngineNotes")}
                      value={displayVehicle.exchangeEngine.notes}
                    />
                  </div>
                </>
              ) : null}
            </FormCardSection>
          </ReadOnlyDetails>
        </FormCard>

        <FormCard title={t("fab:BuyInDetails")} contentClassName="grid gap-3 md:grid-cols-2">
          <FormField
            control={form.control}
            name="type"
            rules={required}
            render={({ field }) => (
              <FormItem>
                <FormLabel required>{t("fab:VehicleBuyInType")}</FormLabel>
                <Select onValueChange={handleVehicleBuyInTypeChange} value={field.value ?? ""}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t("fab:SelectVehicleBuyInType")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {vehicleBuyInTypeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          {buyInType === VehicleBuyInType.TradeIn ? (
            <Field label={t("fab:TradeInReference")} error={errors.tradeInReference?.message}>
              {(controlProps) => <Input maxLength={255} {...form.register("tradeInReference")} {...controlProps} />}
            </Field>
          ) : (
            <div className="hidden md:block" aria-hidden="true" />
          )}
          <FormField
            control={form.control}
            name="purchase.transferDate"
            rules={requiredCurrentOrFutureMonth}
            render={({ field }) => (
              <FormItem>
                <FormLabel required>{t("fab:TransferDate")}</FormLabel>
                <FormControl>
                  <MonthPickerInput
                    ref={field.ref}
                    name={field.name}
                    value={field.value ?? ""}
                    onChange={(value) =>
                      form.setValue("purchase.transferDate", value, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                    onBlur={() => {
                      field.onBlur();
                      void form.trigger("purchase.transferDate");
                    }}
                    pastMonthsDisabled
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="purchase.mileageAtBuyIn"
            rules={requiredNumber}
            render={({ field }) => (
              <FormItem>
                <FormLabel required>{t("fab:MileageAtBuyIn")}</FormLabel>
                <FormControl>
                  <LocalizedNumberInput
                    min={0}
                    integerOnly
                    textAlign={InputTextAlign.Right}
                    name={field.name}
                    ref={field.ref}
                    value={field.value ?? ""}
                    onBlur={field.onBlur}
                    onValueChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Field label={t("fab:VatAmount")}>
            {(controlProps) => (
              <LocalizedNumberInput
                min={0}
                inputMode="decimal"
                prefix="€"
                textAlign={InputTextAlign.Right}
                value={String(vatAmount)}
                onValueChange={() => undefined}
                disabled
                {...controlProps}
              />
            )}
          </Field>
          <FormField
            control={form.control}
            name="purchase.price"
            rules={requiredNumber}
            render={({ field }) => (
              <FormItem>
                <FormLabel required>{t("fab:PurchasePrice")}</FormLabel>
                <FormControl>
                  <LocalizedNumberInput
                    min={0}
                    maxFractionDigits={2}
                    inputMode="decimal"
                    prefix="€"
                    textAlign={InputTextAlign.Right}
                    name={field.name}
                    ref={field.ref}
                    value={field.value ?? ""}
                    onBlur={field.onBlur}
                    onValueChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <p className="flex items-start gap-2 text-sm md:col-span-2">
            <InfoIcon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span>
              {t("fab:VatDeductibleAccordingToValuation")}: {getVehicleBooleanLabel(vatDeductible, t)}
            </span>
          </p>
          <Field label={t("fab:OtherAgreements")} className="md:col-span-2">
            {(controlProps) => <Textarea {...form.register("purchase.otherAgreements")} {...controlProps} />}
          </Field>
        </FormCard>

        <FormCardActions
          cancelLabel={t("common:Cancel")}
          submitLabel={t("fab:CreateAndSendVehicleBuyInContract")}
          isPending={isPending}
          submitDisabled={!isValid || !!branchDeviceError}
          onCancel={onCancel}
        />
        <VehicleBuyInCustomerPrefillDialog
          open={isCustomerPrefillOpen}
          onOpenChange={setIsCustomerPrefillOpen}
          onPrefill={handleCustomerPrefill}
        />
        <VehicleBuyInCustomerNumberDialog
          identifiers={customerIdentifiers}
          open={isCustomerNumberSelectionOpen}
          onOpenChange={setIsCustomerNumberSelectionOpen}
          onSelect={handleCustomerNumberSelect}
        />
      </form>
    </Form>
  );
}
